'use client';

import { Button, cn, InputOTP, InputOTPGroup, InputOTPSlot } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { StatusNotification } from '../../StatusNotification/StatusNotification';
import { FieldMessage } from '../FieldMessage/FieldMessage';
import { useOtpTimer } from './hooks/useOtpTimer';
import { useResendLimiter } from './hooks/useResendLimiter';
import type { OtpInputProps } from './types';

function OtpInput({
  otpLength = 6,
  cooldown = 30,
  maxAttempts = 5,
  resetTime = 60 * 60 * 1000,
  storageKey,
  onResend,
  onOtpChange,
  onCooldownComplete,
  startCooldownOnMount = false,
  maxAttemptsReachedOverride = false,
  helpMessage,
  helpMessageType,
  ...labellingProps
}: OtpInputProps) {
  const t = useTranslations('form.otp');
  const [otp, setOtp] = useState(Array(otpLength).fill(''));
  const [timer, startTimer] = useOtpTimer(cooldown, onCooldownComplete);
  const { maxAttemptsReached, incrementAttempts } = useResendLimiter({
    maxAttempts,
    resetTime,
    storageKey,
  });
  const isMaxAttemptsReached = maxAttemptsReached || maxAttemptsReachedOverride;

  useEffect(() => {
    if (startCooldownOnMount) {
      startTimer();
    }
  }, [startCooldownOnMount, startTimer]);

  const handleOtpChange = (value: string) => {
    setOtp(value.split('').concat(Array(otpLength).fill('')).slice(0, otpLength));

    if (onOtpChange) {
      onOtpChange(value);
    }
  };

  const handleResend = async () => {
    if (timer > 0 || isMaxAttemptsReached) return;

    try {
      await onResend?.();
      incrementAttempts();
      startTimer();
      setOtp(Array(otpLength).fill(''));
      onOtpChange?.('');
    } catch {
      // Error handling (including 429) is done by the caller.
    }
  };

  // Design system specific classes
  const DESIGN_SYSTEM_CLASSES = cn('h-12.5 w-12.5 py-2 px-4 border-border', helpMessageType === 'error' && 'border-error-foreground');

  return (
    <div>
      <InputOTP {...labellingProps} maxLength={otpLength} value={otp.join('')} onChange={handleOtpChange} data-testid="otp-input">
        <InputOTPGroup data-track-name="OTP" data-track-privacy="Mask">
          {Array.from({ length: otpLength }).map((_, index) => (
            <InputOTPSlot key={index} index={index} className={DESIGN_SYSTEM_CLASSES} data-track-name={`OTP${index + 1}`} data-track-privacy="Mask" />
          ))}
        </InputOTPGroup>
      </InputOTP>
      <FieldMessage message={helpMessage} messageType={helpMessageType} data-track-helper="OTP" />
      {isMaxAttemptsReached ? (
        <div className="mt-8">
          <StatusNotification type="warning" description={t('maxAttemptsReached')} dataTrackType="OTP Validation" dataTrackMessage="Maximum Attempts" />
        </div>
      ) : (
        <div className="mt-8 flex items-center whitespace-nowrap" data-testid="resend-div">
          <span className="paragraph-explanatory-strong">{t('didntReceiveCode')}</span>
          <Button
            type="button"
            onClick={handleResend}
            disabled={timer > 0 || isMaxAttemptsReached}
            variant="ghost"
            size="s"
            className={timer > 0 ? 'cursor-not-allowed pointer-events-none' : 'cursor-pointer underline'}
            data-testid="resend-button"
            data-track-name="Resend"
          >
            {timer > 0 ? t('resendCodeIn', { seconds: timer }) : t('resendCode')}
          </Button>
        </div>
      )}
    </div>
  );
}

export { OtpInput };
